import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { UserDataService } from '../../../shared/services/userData.service';
import { IThreat } from '../../../shared/models/threat';
import { ThreatService } from '../../../shared/services/threat.service';
import { FormsModule } from '@angular/forms';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { CoursesOfActionService } from '../../../shared/services/coursesOfAction.service';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { IUserInfo } from '../../../shared/models/contact';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'app-threats-hazards-courses-of-action',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    PrvNextButtonsComponent,
    PopupMessageComponent,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './threats-hazards-courses-of-action.component.html',
  styleUrl: './threats-hazards-courses-of-action.component.css'
})
export class ThreatsHazardsCoursesOfActionComponent implements OnInit {
  sectionTypeId = ESectionTypes["Threat/Hazard - Courses of Action"];
  subSectionTypeId = null;

  loading: boolean = false;
  showFunctionDetails: boolean = false;

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  threatsList: IThreat[] = [];
  selectedThreat: IThreat | null = null;
  latestUpdate: any = {}; // Latest update for the building

  allCourseOfAction: any[] = [];
  initialThreatCourseOfActionForm = {
    before_description: '',
    during_description: '',
    after_description: ''
  };  
  selectedThreatCoursesOfAction: any[] = [];
  threatCourseOfActionForm = {
    before_description: '',
    during_description: '',
    after_description: ''
  };


  constructor(
    private userDataService: UserDataService,
    private threatService: ThreatService,
    private coursesOfActionService: CoursesOfActionService,
    public eopNavigationService: EopNavigationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadThreats();
  }
  
  loadThreats(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.threatService.getThreats(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          console.log(response)
          this.threatsList = response.data ? response.data : [];
          // Sort the list after retrieving threats list.
          if (this.threatsList.length > 0) {
            this.threatsList.sort((a: IThreat, b: IThreat) => a.threat!.localeCompare(b.threat!));
            // assign initial value for the threat dropdown
            if (this.selectedThreat === null) {
              this.selectedThreat = this.threatsList[0];
            }
          }
          this.loadCourseOfAction();
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading contacts:', error.message);
        }
      })
    }
  }

  loadCourseOfAction(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.coursesOfActionService.getCourseOfAction(this.currentBuildingId, 'threat').subscribe({
        next: (response: any) => {
          this.loading = false;
          console.log('Successfully retrieving Course of Action: ', response);
          this.allCourseOfAction = response.data ? response.data : null;
          if (this.allCourseOfAction) {
            this.latestUpdate = this.getLatestUpdate(this.allCourseOfAction);
            this.filterCourseOfAction();

            if (this.selectedThreatCoursesOfAction.length > 0) {
              const formValues = {
                before_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.before_description || '',
                during_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.during_description || '',
                after_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.after_description || ''
              };
              
              this.threatCourseOfActionForm = { ...formValues };
              this.initialThreatCourseOfActionForm = { ...formValues };
            }
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading course of action:', error.message);
        }
      })
    }
  }

  // get latest update for the building
  getLatestUpdate(objectList: any[]): { last_updated_at: string; last_updated_user_name: string } | null {
    if (!objectList || objectList.length === 0) return null;
  
    const latest = objectList.reduce((latest, current) => {
      const latestDate = new Date(latest.courses_of_action?.last_updated_at || 0);
      const currentDate = new Date(current.courses_of_action?.last_updated_at || 0);
      return currentDate > latestDate ? current : latest;
    });
  
    return {
      last_updated_at: latest.courses_of_action?.last_updated_at || '',
      last_updated_user_name: latest.courses_of_action?.last_updated_user_name || ''
    };
  }

  hasFormChanged(): boolean {
    return (
      this.threatCourseOfActionForm.before_description !== this.initialThreatCourseOfActionForm.before_description ||
      this.threatCourseOfActionForm.during_description !== this.initialThreatCourseOfActionForm.during_description ||
      this.threatCourseOfActionForm.after_description !== this.initialThreatCourseOfActionForm.after_description
    );
  } 

  // Filter courses of action based on the selected threat
  // default selected threat is the first one in the threat list above
  filterCourseOfAction(): void {
    if (this.allCourseOfAction && this.selectedThreat) {
      const functionId = Number(this.selectedThreat.id);
      this.selectedThreatCoursesOfAction = this.allCourseOfAction
        .filter(item => Number(item.id) === functionId);
    }
  }

  setFunction(selecteItem: any): void {
    this.selectedThreat = selecteItem;
    this.loadCourseOfAction();

    this.threatCourseOfActionForm = {
      before_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.before_description || '',
      during_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.during_description || '',
      after_description: this.selectedThreatCoursesOfAction[0]?.courses_of_action?.after_description || ''
    };
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.selectedThreat) {
      this.loading = true;
      console.log('Submitting:', this.threatCourseOfActionForm);
      this.coursesOfActionService.saveCourseOfAction(this.currentBuildingId, 'threat', String(this.selectedThreat.id), this.threatCourseOfActionForm).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating courses of action for a threat.';
            this.loadCourseOfAction(); // reload to get the latest data
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
          console.error('Error saving course of action:', error);
        }
      })
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
  }
}
